var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography';
import { SponsorshipTerms } from 'root/src/client/v2/drop/components/SponsorshipTerms/SponsorshipTerms';
import { sponsorshipRegularTerms } from 'root/src/shared/descriptions/modules/howToEarnPointsModal';
import { useDropShortUrl } from 'root/src/client/v2/drop/hooks/useDropShortUrl';
import { TextField } from 'root/src/client/v2/common/components/form/TextField';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { useDropEvent } from 'root/src/client/logic/dropEvent/hooks/useDropEvent';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import { MAX_CREATE_POINTS_APPLICATION_LENGTH } from 'root/src/server/modules/points/endpoints/createPointsApplication.endpoint';
import { dropEndpointIds } from 'root/src/shared/drops/drops.constants';
import { pointsEndpointIds } from 'root/src/shared/points/points.constants';
export const PointsApplicationModal = () => {
    const drop = useDropEvent();
    if (!drop) {
        return null;
    }
    return <PointsApplicationModalContent drop={drop}/>;
};
const PointsApplicationModalContent = ({ drop }) => {
    const { shortUrl, toggleIsTermsAccepted, isTermsAccepted, comment, setComment, applyForPoints, isLoading, isCommentError, } = usePointsApplicationModal(drop);
    return (<ModalLayout>
			<div className="flex flex-col justify-center items-center max-w-[700px] min-w-[350px]">
				<div className="flex flex-col gap-1 justify-center items-center ">
					<Typography variant={typographyVariants.heading2}>
						Apply for points
					</Typography>
				</div>
				<div className="border-[0.5px] w-[144px] border-light-4 mb-[30px] mt-[30px]"/>
				<div className="text-xl m-[25px]">
					We’ll approve your points application within 48 hours.
					You need to be approved to earn points.
					Let us know why you think you’re a good fit:
				</div>
				<TextField multiline rows={3} className="w-[350px]" invalid={isCommentError} value={comment} onChange={(e) => {
            setComment(e.target.value);
        }} placeholder="Type here"/>
				<div className="flex w-[350px]">
					<span className={`m-1 ml-auto font-medium ${isCommentError ? 'text-[#d32f2f]' : ''}`}>{comment.length} / {MAX_CREATE_POINTS_APPLICATION_LENGTH}</span>
				</div>
				<div className="border-[0.5px] w-[144px] border-light-4 mb-[30px] mt-[30px]"/>
				<SponsorshipTerms terms={sponsorshipRegularTerms} dropEvent={drop} shortUrl={shortUrl} canEarnPoints isTermsAccepted={false} canUserReclaimReservation={false}/>
				<div className="flex items-center cursor-pointer mb-4" onClick={toggleIsTermsAccepted}>
					<input type="checkbox" className="w-[14px] h-[14px] m-2" checked={isTermsAccepted}/>
					<Typography variant={typographyVariants.mini}>
						I agree to the terms for earning Points on this Drop.
					</Typography>
				</div>
				<ButtonNew disabled={!isTermsAccepted || isLoading} className="w-[200px] mb-5" onClick={() => applyForPoints()}>
					Apply for points
				</ButtonNew>
			</div>
		</ModalLayout>);
};
const usePointsApplicationModal = (drop) => {
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [comment, setComment] = useState('');
    const { fetcher } = useDareDropApi();
    const modal = useModal();
    const shortUrlQuery = useDropShortUrl({ dropId: drop.id, enabled: false });
    const dispatch = useAppDispatch();
    const isCommentError = comment.length > MAX_CREATE_POINTS_APPLICATION_LENGTH;
    const toggleIsTermsAccepted = () => {
        setIsTermsAccepted(!isTermsAccepted);
    };
    const applyForPointsMutation = useMutation({
        mutationKey: [pointsEndpointIds.CREATE_POINTS_APPLICATION],
        mutationFn: () => __awaiter(void 0, void 0, void 0, function* () {
            const res = yield fetcher({
                endpointId: pointsEndpointIds.CREATE_POINTS_APPLICATION,
                payload: {
                    dropId: drop.id,
                    comment: comment !== null && comment !== void 0 ? comment : undefined,
                },
            });
            yield dispatch(apiRequest(dropEndpointIds.DROP_GET_EVENT, { recordId: drop.mnemonicId }));
            modal.hide();
            return res;
        }),
    });
    return {
        shortUrl: shortUrlQuery.data,
        isTermsAccepted,
        toggleIsTermsAccepted,
        comment,
        setComment,
        applyForPoints: applyForPointsMutation.mutateAsync,
        isLoading: applyForPointsMutation.isPending,
        isCommentError,
    };
};
